import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import ReactDOM from 'react-dom/client';
import { Box, TextField, Button, Typography, Tooltip, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';

const JumpToUI = ({ map, onJump }) => {
    const [lat, setLat] = useState('');
    const [lon, setLon] = useState('');
    const [error, setError] = useState('');

    const handleJump = () => {
        const latNum = parseFloat(lat);
        const lonNum = parseFloat(lon);

        if (isNaN(latNum) || isNaN(lonNum) || latNum < -90 || latNum > 90 || lonNum < -180 || lonNum > 180) {
            setError('Invalid coordinates');
            return;
        }

        setError('');
        map.setView([latNum, lonNum], map.getZoom());
        if (onJump) {
            onJump({ lat: latNum, lng: lonNum });
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-end',
        }}>
            <Box sx={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: '12px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.25)',
                width: '140px',
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>Go to</Typography>
                <Tooltip title="In decimal degrees, e.g. Lat: 35.1432, Lon: 33.3507" placement="left">
                    <IconButton size="small" sx={{ 
                        padding: '2px',
                        '&:hover': {
                            backgroundColor: 'rgba(33, 147, 176, 0.1)',
                        }
                    }}>
                        <InfoIcon fontSize="small" sx={{ color: '#2193b0' }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <TextField 
                    label="Latitude" 
                    variant="standard" 
                    size="small" 
                    value={lat} 
                    onChange={e => setLat(e.target.value)} 
                    error={!!error}
                    sx={{
                        '& .MuiInput-underline:before': {
                            borderBottomColor: 'rgba(33, 147, 176, 0.3)',
                        },
                        '& .MuiInput-underline:hover:before': {
                            borderBottomColor: 'rgba(33, 147, 176, 0.5)',
                        },
                        '& .MuiInput-underline:after': {
                            borderBottomColor: '#2193b0',
                        },
                    }}
                />
                <TextField 
                    label="Longitude" 
                    variant="standard" 
                    size="small" 
                    value={lon} 
                    onChange={e => setLon(e.target.value)} 
                    error={!!error}
                    sx={{
                        '& .MuiInput-underline:before': {
                            borderBottomColor: 'rgba(33, 147, 176, 0.3)',
                        },
                        '& .MuiInput-underline:hover:before': {
                            borderBottomColor: 'rgba(33, 147, 176, 0.5)',
                        },
                        '& .MuiInput-underline:after': {
                            borderBottomColor: '#2193b0',
                        },
                    }}
                />
                <Button 
                    variant="contained" 
                    size="small" 
                    onClick={handleJump}
                    sx={{
                        background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1a7a93 0%, #5bc3de 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.25)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        }
                    }}
                >
                    Go
                </Button>
            </Box>
            {error && <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>{error}</Typography>}
            </Box>
            <Box sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: '10px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.25)',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.35)',
                }
            }}>
                <img 
                    src="/logoPNG3eng.png" 
                    alt="LIRA Logo" 
                    style={{ 
                        height: '45px',
                        display: 'block',
                        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                    }} 
                />
            </Box>
        </Box>
    );
};

const JumpToCoordinates = ({ onJump }) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const control = L.control({ position: 'bottomleft' });

        control.onAdd = () => {
            const div = L.DomUtil.create('div', 'leaflet-jump-to-coords');
            L.DomEvent.disableClickPropagation(div);
            L.DomEvent.disableScrollPropagation(div);
            const root = ReactDOM.createRoot(div);
            root.render(<JumpToUI map={map} onJump={onJump} />);
            div.reactRoot = root;
            return div;
        };

        control.onRemove = () => {
            const div = control.getContainer();
            if (div && div.reactRoot) {
                queueMicrotask(() => {
                    if (div.reactRoot) {
                        div.reactRoot.unmount();
                        delete div.reactRoot;
                    }
                });
            }
        };

        control.addTo(map);

        return () => {
            if (map && control) {
                map.removeControl(control);
            }
        };
    }, [map, onJump]);

    return null;
};

export default JumpToCoordinates;
